import type { ApolloCache } from "@apollo/client";

import { isValidGqlFragment } from "./isValidGqlFragment";
import { TagMinimalFragment } from "./queries/fragments/tagFragment.graphql";
import { getTagsForEntity as getTagsForEntityQuery } from "./queries/tags.graphql";

export type TagValueCacheFields = {
  tagId: string;
  attributes: Record<string, unknown>;
  displayValue?: string;
  entityId?: string;
  tagTypes?: string[];
  entityType?: string;
};

type CachedTagRecord = {
  id?: string;
  tagId?: string;
  displayValue?: string;
  attributes?: Record<string, unknown>;
  tag?: CachedTagRecord;
};

type GetTagsForEntityData = {
  tags?: CachedTagRecord[];
};

const matchesSavedTag = (entityTag: CachedTagRecord, tagId: string) =>
  entityTag.tagId === tagId ||
  entityTag.tag?.id === tagId ||
  entityTag.id === tagId;

const withSavedTagValue = (
  entityTag: CachedTagRecord,
  attributes: Record<string, unknown>,
  displayValue?: string
): CachedTagRecord => {
  const nextTag: CachedTagRecord = {
    ...entityTag,
    attributes,
  };
  if (displayValue !== undefined) {
    nextTag.displayValue = displayValue;
  }
  if (entityTag.tag) {
    nextTag.tag = withSavedTagValue(entityTag.tag, attributes, displayValue);
  }
  return nextTag;
};

/**
 * Persist saved tag attributes (and optional display value) into the Apollo
 * cache. Mutation payloads can omit or empty `attributes`; without this write,
 * info/read views keep showing missing or stale values until a full reload.
 */
export const updateTagValueInCache = (
  cache: ApolloCache,
  {
    tagId,
    attributes,
    displayValue,
    entityId,
    tagTypes,
    entityType,
  }: TagValueCacheFields
): void => {
  const tagCacheId = cache.identify({ __typename: "Tag", id: tagId });
  if (tagCacheId) {
    cache.modify({
      id: tagCacheId,
      fields: {
        attributes: () => attributes,
        ...(displayValue !== undefined
          ? { displayValue: () => displayValue }
          : {}),
      },
    });
  }

  if (isValidGqlFragment(TagMinimalFragment)) {
    try {
      const existing = cache.readFragment<CachedTagRecord>({
        id: `Tag:${tagId}`,
        fragment: TagMinimalFragment,
        fragmentName: "TagMinimalFragment",
      });
      if (existing) {
        cache.writeFragment({
          id: `Tag:${tagId}`,
          fragment: TagMinimalFragment,
          fragmentName: "TagMinimalFragment",
          data: {
            ...existing,
            attributes,
            ...(displayValue !== undefined ? { displayValue } : {}),
          },
        });
      }
    } catch {
      // Incomplete Tag fragment — still patch getTagsForEntity below.
    }
  }

  if (!entityId || !tagTypes?.length) {
    return;
  }

  const variables = {
    entityId,
    tagTypes,
    ...(entityType ? { entityType } : {}),
  };

  let cached: GetTagsForEntityData | null = null;
  try {
    cached = cache.readQuery<GetTagsForEntityData>({
      query: getTagsForEntityQuery,
      variables,
    });
  } catch {
    cached = null;
  }

  if (!cached?.tags) {
    return;
  }

  cache.writeQuery({
    query: getTagsForEntityQuery,
    variables,
    data: {
      tags: cached.tags.map((entityTag) =>
        matchesSavedTag(entityTag, tagId)
          ? withSavedTagValue(entityTag, attributes, displayValue)
          : entityTag
      ),
    },
  });
};
